import {fetch, post, put, remove} from '@/base-http';
import User from '@/modules/user/model/User';
import _ from 'lodash'
import Pagination, {PaginationInput} from '@/modules/core/model/pagination';
import KeyValuePair from '@/modules/core/support/KeyValuePair';
import Session from '@/modules/user/model/Session';
import UserGroup from '@/modules/user/model/UserGroup';

/**
 * Resultado de eliminar usuarios de un grupo
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
export interface DeleteGroupUserResult {
    /**
     * Ids de los usuarios argumentados en el proceso que la aplicación intento
     * eliminar del grupo pero no existen
     */
    usersNotFound: number[]

    /**
     * Ids de los usuarios que la aplicación intenta eliminar del grupo pero que
     * no están asociados a este.
     */
    usersNotInGroup: number[]

    /**
     * Ids de los usuarios que pudieron ser eliminados del grupo de forma correcta
     */
    deletedUsers: number[]
}

/**
 * Resultado de eliminar usuarios de un grupo
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
export interface AddUsersToGroupResult {
    /**
     * Ids de los usuarios argumentados en el proceso que la aplicación intento
     * añadir al grupo pero que no existen en el sistema
     */
    usersNotFound: number[]

    /**
     * Ids de los usuarios que la aplicación intento añadir al grupo pero que se
     * encuentra registrados en este.
     */
    usersAlreadyInGroup: number[]

    /**
     * Ids de los usuarios que pudieron ser añadidos al grupo de forma correcta
     */
    addedUsers: number[]
}

/**
 * Criterios de búsqueda de usuarios
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
export interface FindUserCriteria {
    /**
     * Filtro para buscar usuario por coincidencia de nombre, email, id o todos a la vez
     */
    searchBy?: {
        field: 'id' | 'name' | 'email' | 'all',
        value: string | number,
    }
    /**
     * Filtro para obtener únicamente los usuarios que se encuentran en los grupos
     * argumentados
     */
    includeGroups?: number[],

    /**
     * Filtro para obtener únicamente los usuarios que no se encuentran en los grupos argumentados
     */
    excludeGroups?: number[],
}

/**
 * Datos de entrada para la paginación de usuarios
 */
export interface UserPaginationInput extends PaginationInput<FindUserCriteria> {
}


/**
 * Obtiene un usuario desde la API de Artisan
 *
 * @param id
 */
export async function getUser (id: number) {
    try {
        const data = await fetch(`/management/users/${id}`)

        return User.deserialize(data)
    } catch (e) {
        throw new Error(_.get(e, 'response.data.message', e.message))
    }
}

/**
 * Obtiene una paginación de usuarios de la API
 * @param input
 */
export async function getUserPagination (input: UserPaginationInput): Promise<Pagination<User>> {
    const params: KeyValuePair = {
        page: input.page,
        recordsPerPage: input.recordsPerPage,
        orderBy: _.get(input, 'orderBy.field', 'id'),
        orderByDirection: _.get(input, 'orderBy.direction', 'desc'),
    }

    const filters = input.filters || {}
    const includeGroups = filters.includeGroups
    if (includeGroups && includeGroups.length) {
        params['groups'] = includeGroups.join(',')
    }

    const excludeGroups = filters.excludeGroups
    if (excludeGroups && excludeGroups.length) {
        params['excludeGroups'] = excludeGroups.join(',')
    }

    const searchBy = filters!.searchBy
    if (searchBy && searchBy.value) {
        params[searchBy.field || 'all'] = searchBy.value
    }

    let data: any
    try {
        data = await fetch(`/management/users-pagination`, {params})
    } catch (e) {
        throw new Error(_.get(e, 'response.data.message', e.message))
    }

    if (!Array.isArray(data.records)) {
        throw new Error('Se esperaba un array con la lista de usuarios')
    }

    return {
        items: data.records.map((record: KeyValuePair) => User.deserialize(record)),
        totalRecords: data.totalRecords,
        totalPages: data.totalPages,
    }
}

/**
 * Solicita al servidor la creación de un nuevo usuario
 *
 * @param user
 */
export async function createUser (user: User) {
    if (!user.isNew) {
        throw new Error(`El usuario con id ${user.id} ya se encuentra registrado`)
    }

    try {
        const result = await post('/management/user', user.serialize())

        return User.deserialize(result)
    } catch (error) {
        throw new Error(_.get(error, 'response.message', error.message))
    }
}

/**
 * Actualiza la información de un usuario en el servidor
 *
 * @param user
 */
export async function updateUser (user: User) {
    if (user.isNew) {
        throw new Error('No se puede actualizar un usuario que todavía no ha sido registrado')
    }

    const result = await put(`/management/users/${user.id}`, user.serialize())

    return User.deserialize(result)
}

export interface ChangePasswordInput {
    user: number
    password: string
    confirm: string
    clearSessions: boolean
}

/**
 * Actualiza la contraseña de un usuario
 *
 * @param input
 */
export async function changePassword (input: ChangePasswordInput) {
    const {user, password, confirm, clearSessions} = input
    const result = await put(`/management/users/${user}/password`, {
        password,
        password_confirm: confirm,
        clearSessions: input.clearSessions,
    })

    return true
}

export interface UserAssociation {
    key: string
    count: number
    registryName: string
}

export class DeleteUserRejection extends Error {
    public associations: UserAssociation[] = []
    public reason = ''
}

export enum DeleteUserRejectionReason {
    UserHAsAssociations = 'USER_HAS_ASSOC',
    UserNotFound = 'USER_NOT_FOUND',
}

/**
 * Solicita al servidor la eliminación de un usuario
 *
 * @param user
 */
export async function deleteUser (user: number) {
    try {
        const result = await remove(`/management/users/${user}`)

        return result.success || true
    } catch (error) {
        const data = error.response.data
        const rejection = new DeleteUserRejection(data.message)
        rejection.reason = data.reason
        // El servidor ha respondido con un Bad Request detallando que el usuario que ha se ha intentado
        // eliminar tiene otros registros asociados que deben ser eliminado o re-asignados manualmente
        if (data.reason === 'USER_HAS_ASSOC') {
            rejection.associations = data.associations.map((assoc: any) => ({
                key: assoc.key,
                count: assoc.count,
                registryName: assoc.registry_name,
            }))
        }

        throw rejection
    }
}

/**
 * Solicita al servidor una lista paginada de sesiones de usuario
 *
 * @param input
 */
export async function getSessionPagination (input: PaginationInput<{ user: number | undefined }>) {
    const params: KeyValuePair = {
        page: input.page || 1,
        recordsPerPage: input.recordsPerPage || 20,
    }

    if (_.has(input, 'filters.user')) {
        params['user'] = input.filters!.user
    }

    const pagination = await fetch('/management/sessions', {params})

    return {
        totalRecords: pagination.total_records,
        totalPages: pagination.total_pages,
        items: pagination.sessions.map((s: KeyValuePair) => Session.deserialize(s)),
    } as Pagination<Session>
}

export interface DeleteSessionsCriteria {
    ids?: number[]
    user?: number
}

export async function deleteSessions (criteria: DeleteSessionsCriteria) {
    const params: KeyValuePair = {}
    if ('user' in criteria) {
        params['user'] = criteria.user
    }

    if ('ids' in criteria && criteria.ids) {
        params['ids'] = criteria.ids!.join(',')
    }

    const result = await remove('/management/sessions', {params})

    return {
        success: true,
        deletedSessions: result.deleted_sessions,
    }
}

/**
 * Solicita al servidor que indique si el email argumentado se encuentra disponible
 * para ser registrado como correo de un nuevo usuario
 *
 * @param email
 */
export async function checkEmailAvailability (email: string) {
    const result = await fetch('/management/user/email_availability', {params: {email}})
    return result.available as boolean
}

/**
 * Solicita al servidor la lista de todos los grupos de usuarios registrados
 * en la aplicación
 */
export async function listUserGroups () {
    const data = await fetch('/management/user-groups')
    if (!Array.isArray(data)) {
        throw new Error(`Se esperaba un array con la lista de grupos de usuario, se obtuvo ${typeof data}`)
    }
    return data.map((u: KeyValuePair) => UserGroup.deserialize(u))
}

/**
 * Solicita al servidor un grupo de usuarios registrado en la aplicación
 *
 * @param id
 */
export async function getUserGroup (id: number | string) {
    const params: any = {}
    // El ID es una ruta de acceso del arbol de grupos, se codifica en base64
    // para compatibilidad con rutas
    if (typeof id === 'string') {
        id = btoa(id)
        params.encoding = 'base64'
    }

    const data = await fetch(`/management/user-groups/${id}`, { params })

    return UserGroup.deserialize(data)
}

/**
 * Añade una lista de usuarios a un grupo
 *
 * @param group
 * @param users
 */
export async function addUsersToGroup (group: number, users: number[]) {
    const result = await post(`/management/user-groups/${group}/assigned-users`, {
        users,
    })

    return {
        usersNotFound: result.users_not_found,
        usersAlreadyInGroup: result.users_already_in_group,
        addedUsers: result.users_added,
    } as AddUsersToGroupResult
}

/**
 * Elimina del grupo argumentado la lista de usuarios asociados a los identificadores
 * argumentado
 *
 * No elimina los usuarios del sistema, desvincula el usuario y el grupo
 *
 * @param group
 * @param users
 */
export async function deleteGroupUsers (group: number, users: number[]) {
    const result = await remove(`/management/user-groups/${group}/assigned-users`, {
        params: {
            users: users.join(','),
        },
    })


    return {
        usersNotFound: result.users_not_found,
        usersNotInGroup: result.users_not_in_group,
        deletedUsers: result.deleted_users,
    } as DeleteGroupUserResult
}


/**
 * Registra un nuevo grupo de usuarios en la aplicación
 *
 * @param group
 */
export async function createUserGroup (group: UserGroup) {
    if (!group.isNew) {
        throw new Error('No se puede insertar un grupo de usuarios que existe en la aplicación')
    }

    const data = await post('/management/user-group', group.serialize())

    return UserGroup.deserialize(data)
}

/**
 * Actualiza la información de un grupo de usuarios
 *
 * @param group
 */
export async function updateUserGroup (group: UserGroup) {
    if (group.isNew) {
        throw new Error('No se puede actualizar un grupo de usuarios que aun no existe en la aplicación')
    }

    const data = await put(`/management/user-groups/${btoa(group.path)}`, {
        name: group.name,
        attributes: group.attributes,
        parent: group.parent ? group.parent.id : null,
        children: group.children.map(c => c.id),
    }, { params: { encoding: 'base64'} })

    return UserGroup.deserialize(data)
}


/**
 * Solicita a la aplicación la eliminación del grupo de usuarios asociado al
 * identificador argumentado
 *
 * @param id
 */
export async function deleteUseGroup (id: number, isDeleteAllUser: boolean): Promise<boolean> {
    const deleteUsers = isDeleteAllUser ? 1 : 0
    const result = await remove(`/management/user-groups/${id}?delete_users=${deleteUsers}`)

    return result.success || false
}
